import { el } from "../../utils/dom";
import { getState, setState } from "../../state/store";

interface WizardStep {
    title: string;
    content: string;
    targetSelector?: string;  // CSS selector to highlight
    position?: 'center' | 'bottom' | 'top';
}

const WIZARD_STEPS: WizardStep[] = [
    {
        title: "Welcome to Group Generator! 👋",
        content: "This app helps you create balanced classroom groups and seating arrangements. Let's take a quick tour of the main features.",
        position: 'center'
    },
    {
        title: "Step 1: Add Your Students",
        content: "Click the 'Roster' button to import your students. You can paste names from a spreadsheet or add them individually. You can also assign tags and skill levels to each student.",
        targetSelector: "#btn-roster",
        position: 'bottom'
    },
    {
        title: "Step 2: Configure Settings",
        content: "Click 'Settings' to set your target group size, skill balancing preferences, and how you want tags to be handled. You can also save your settings as presets for quick reuse.",
        targetSelector: "#btn-settings",
        position: 'bottom'
    },
    {
        title: "Step 3: Set Constraints",
        content: "Use the sidebar to add constraints. 'Must be together' keeps students in the same group. 'Must be apart' ensures they're separated. These help manage classroom dynamics.",
        targetSelector: "#constraints",
        position: 'top'
    },
    {
        title: "Step 4: Generate Groups",
        content: "Click the green 'Generate' button to create balanced groups. The algorithm considers group sizes, skill levels, tags, and your constraints to create optimal groupings.",
        targetSelector: "#btn-generate",
        position: 'bottom'
    },
    {
        title: "Seating Mode",
        content: "Switch to Seating Mode using the toggle in the sidebar to create seating arrangements. You can design your room layout and set proximity constraints for where students should sit.",
        targetSelector: "#mode-toggle",
        position: 'top'
    },
    {
        title: "You're All Set! 🎉",
        content: "That's the basics! Remember: use Ctrl+Z to undo, click '?' for keyboard shortcuts, and the export button to save your work. Happy grouping!",
        position: 'center'
    }
];

/**
 * Check if onboarding should be shown and show it if needed
 */
export function checkAndShowOnboarding(): void {
    const s = getState();

    // Check localStorage for completion flag
    if (s.onboardingComplete || localStorage.getItem("GG:v1:onboardingComplete") === "true") {
        return;
    }

    // Show after a short delay to let the UI render
    setTimeout(() => showOnboardingWizard(), 500);
}

/**
 * Show the onboarding wizard
 */
export function showOnboardingWizard(): void {
    let currentStep = 0;
    let highlightEl: HTMLElement | null = null;

    const backdrop = el("div", {
        style: `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.7);
            z-index: 2000;
            display: flex;
            align-items: center;
            justify-content: center;
        `
    });

    const dialog = el("div", {
        style: `
            background: var(--card);
            border-radius: 16px;
            padding: 24px;
            max-width: 450px;
            width: 90%;
            border: 2px solid var(--primary);
            box-shadow: 0 4px 24px rgba(0,0,0,0.3);
            position: relative;
        `
    });

    const progressBar = el("div", {
        style: "height: 4px; background: var(--border); border-radius: 2px; margin-bottom: 16px; overflow: hidden;"
    });
    const progressInner = el("div", {
        style: `height: 100%; background: var(--primary); transition: width 0.3s ease; width: 0%;`
    });
    progressBar.appendChild(progressInner);

    const titleEl = el("h2", { style: "margin: 0 0 12px 0;" });
    const contentEl = el("p", { style: "margin: 0 0 20px 0; line-height: 1.6;" });

    const buttonsRow = el("div", { style: "display: flex; gap: 8px; justify-content: space-between;" });

    const skipBtn = el("button", { style: "opacity: 0.7;" }, "Skip Tutorial");
    const prevBtn = el("button", {}, "← Back");
    const nextBtn = el("button", { class: "primary" }, "Next →");

    function updateStep() {
        const step = WIZARD_STEPS[currentStep];
        if (!step) return;

        // Update progress
        progressInner.style.width = `${((currentStep + 1) / WIZARD_STEPS.length) * 100}%`;

        // Update content
        titleEl.textContent = step.title;
        contentEl.textContent = step.content;

        // Update buttons
        prevBtn.style.display = currentStep === 0 ? "none" : "";
        nextBtn.textContent = currentStep === WIZARD_STEPS.length - 1 ? "Get Started! 🚀" : "Next →";

        // Clear previous highlight
        if (highlightEl) {
            highlightEl.style.boxShadow = "";
            highlightEl.style.zIndex = "";
            highlightEl.style.position = "";
            highlightEl = null;
        }

        // Position dialog and highlight target
        if (step.targetSelector) {
            const target = document.querySelector(step.targetSelector) as HTMLElement;
            if (target) {
                highlightEl = target;
                target.style.boxShadow = "0 0 0 4px var(--primary), 0 0 20px var(--primary)";
                target.style.zIndex = "2001";
                target.style.position = "relative";

                // Position dialog near target
                const rect = target.getBoundingClientRect();
                dialog.style.position = "fixed";

                if (step.position === 'bottom') {
                    dialog.style.top = `${rect.bottom + 20}px`;
                    dialog.style.left = `${Math.max(20, Math.min(rect.left, window.innerWidth - 470))}px`;
                } else if (step.position === 'top') {
                    dialog.style.bottom = `${window.innerHeight - rect.top + 20}px`;
                    dialog.style.top = "auto";
                    dialog.style.left = `${Math.max(20, Math.min(rect.left, window.innerWidth - 470))}px`;
                } else {
                    dialog.style.top = "50%";
                    dialog.style.left = "50%";
                    dialog.style.transform = "translate(-50%, -50%)";
                }
            }
        } else {
            dialog.style.position = "relative";
            dialog.style.top = "";
            dialog.style.left = "";
            dialog.style.bottom = "";
            dialog.style.transform = "";
        }
    }

    function nextStep() {
        if (currentStep < WIZARD_STEPS.length - 1) {
            currentStep++;
            updateStep();
        } else {
            complete();
        }
    }

    function prevStep() {
        if (currentStep > 0) {
            currentStep--;
            updateStep();
        }
    }

    function complete() {
        // Clear highlight
        if (highlightEl) {
            highlightEl.style.boxShadow = "";
            highlightEl.style.zIndex = "";
            highlightEl.style.position = "";
        }

        // Mark as complete
        setState(ns => { ns.onboardingComplete = true; });
        localStorage.setItem("GG:v1:onboardingComplete", "true");

        backdrop.remove();
    }

    skipBtn.addEventListener("click", complete);
    prevBtn.addEventListener("click", prevStep);
    nextBtn.addEventListener("click", nextStep);

    // Close on Escape
    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            complete();
            document.removeEventListener("keydown", handleKeydown);
        } else if (e.key === "ArrowRight" || e.key === "Enter") {
            nextStep();
        } else if (e.key === "ArrowLeft") {
            prevStep();
        }
    };
    document.addEventListener("keydown", handleKeydown);

    buttonsRow.append(skipBtn, el("div", { style: "display: flex; gap: 8px;" }, prevBtn, nextBtn));
    dialog.append(progressBar, titleEl, contentEl, buttonsRow);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    updateStep();
}

/**
 * Reset onboarding to show it again
 */
export function resetOnboarding(): void {
    setState(ns => { ns.onboardingComplete = false; });
    localStorage.removeItem("GG:v1:onboardingComplete");
}
